import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  useRemoteParticipants
} from '@livekit/components-react';
import '@livekit/components-styles';
import './MeetingPage.css';

const formatStart = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const fetchRoomStatus = (meetingToken) => fetch(
  `/api/calendar/booking/meeting/${encodeURIComponent(meetingToken)}/room-status?ts=${Date.now()}`,
  { cache: 'no-store' }
);

const EmptyRoomOwnerNotify = ({ notifyingOwner, onNotify }) => {
  const remoteParticipants = useRemoteParticipants();

  if (remoteParticipants.length > 0) return null;

  return (
    <div className="meeting-empty-room-cta">
      <div className="meeting-empty-room-cta-card">
        <span>Waiting for the room owner</span>
        <button type="button" onClick={onNotify} disabled={notifyingOwner}>
          {notifyingOwner ? 'Notifying...' : 'Notify room owner I am waiting'}
        </button>
      </div>
    </div>
  );
};

const MeetingPage = () => {
  const { meetingToken } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [status, setStatus] = useState('Loading meeting details...');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [notifyingOwner, setNotifyingOwner] = useState(false);
  const [ownerNotice, setOwnerNotice] = useState('');
  const [tokenData, setTokenData] = useState(null);
  const [roomOwnerResponse, setRoomOwnerResponse] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    setStatus('Loading meeting details...');

    fetch(`/api/calendar/booking/meeting/${encodeURIComponent(meetingToken)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Meeting lookup failed');
        if (!mounted) return;
        const requestedName = typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('name')
          : '';
        setMeeting(data);
        setDisplayName(requestedName || data.guestName || data.hostUsername || '');
        setStatus('Ready to join.');
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message || 'Meeting lookup failed');
        setLoading(false);
      });

    return () => { mounted = false; };
  }, [meetingToken]);

  const meetingUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/meeting/${meetingToken}`;
  }, [meetingToken]);

  const joinMeeting = async () => {
    if (!displayName.trim()) {
      setError('Enter your name before joining.');
      return;
    }
    setJoining(true);
    setError('');
    setStatus('Joining meeting...');
    try {
      const res = await fetch(`/api/calendar/booking/meeting/${encodeURIComponent(meetingToken)}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: displayName.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Meeting token failed');
      setTokenData(data);
      setStatus(`Connected to ${data.room}.`);
    } catch (err) {
      setError(err.message || 'Meeting token failed');
      setStatus('Unable to join right now.');
    } finally {
      setJoining(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(meetingUrl);
      setStatus('Meeting link copied.');
    } catch (err) {
      setError('Could not copy the link.');
    }
  };

  const notifyRoomOwner = async () => {
    if (!displayName.trim()) {
      setError('Enter your name before notifying the room owner.');
      return;
    }
    setNotifyingOwner(true);
    setError('');
    setOwnerNotice('');
    setRoomOwnerResponse(null);
    try {
      const res = await fetch(`/api/calendar/booking/meeting/${encodeURIComponent(meetingToken)}/notify-owner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: displayName.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Room owner notification failed');
      setOwnerNotice(data.message || 'Room owner notified.');
      const statusRes = await fetchRoomStatus(meetingToken);
      const statusData = await statusRes.json().catch(() => ({}));
      if (statusRes.ok && statusData.responseMessage) {
        setRoomOwnerResponse(statusData);
        setOwnerNotice('');
      }
    } catch (err) {
      setError(err.message || 'Room owner notification failed');
    } finally {
      setNotifyingOwner(false);
    }
  };

  useEffect(() => {
    if (!tokenData && !ownerNotice) return undefined;
    let cancelled = false;
    const pollRoomStatus = async () => {
      try {
        const res = await fetchRoomStatus(meetingToken);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;
        if (cancelled || !data.responseMessage) return;
        setRoomOwnerResponse(data);
        setOwnerNotice('');
      } catch (err) {
        // The room still works if status polling fails, so keep this quiet.
      }
    };
    pollRoomStatus();
    const timer = setInterval(pollRoomStatus, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [meetingToken, tokenData, ownerNotice]);

  if (loading) {
    return <div className="meeting-status-screen">Loading meeting...</div>;
  }

  if (error && !meeting) {
    return <div className="meeting-status-screen meeting-error-screen">{error}</div>;
  }

  return (
    <div className="meeting-page-shell">
      <div className="meeting-page-card">
        <div className="meeting-page-sidebar">
          <div className="meeting-badge">Private link</div>
          <h1>{meeting?.title || 'Join meeting'}</h1>
          <p className="meeting-subtitle">
            {meeting?.hostUsername ? `Hosted by ${meeting.hostUsername}` : 'NumFree Meeting'}
          </p>
          <div className="meeting-meta-card">
            <div>
              <span>Starts</span>
              <strong>{formatStart(meeting?.startsAt) || 'TBD'}</strong>
            </div>
            <div>
              <span>Duration</span>
              <strong>{meeting?.duration || 30} min</strong>
            </div>
            <div>
              <span>Room</span>
              <strong>{meeting?.meetingRoom || 'NumFree room'}</strong>
            </div>
          </div>

          {!tokenData ? (
            <div className="meeting-prejoin">
              <label htmlFor="meetingDisplayName">Your name</label>
              <input
                id="meetingDisplayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your display name"
              />
              <div className="meeting-prejoin-actions">
                <button type="button" onClick={joinMeeting} disabled={joining}>
                  {joining ? 'Joining...' : 'Join Room'}
                </button>
                <button type="button" className="secondary" onClick={copyLink}>
                  Copy link
                </button>
                <button type="button" className="secondary" onClick={notifyRoomOwner} disabled={notifyingOwner}>
                  {notifyingOwner ? 'Notifying...' : 'Notify Room Owner'}
                </button>
              </div>
            </div>
          ) : (
            <div className="meeting-live-actions">
              <button type="button" onClick={notifyRoomOwner} disabled={notifyingOwner}>
                {notifyingOwner ? 'Notifying...' : 'Notify Room Owner'}
              </button>
              <button type="button" className="secondary" onClick={copyLink}>
                Copy link
              </button>
            </div>
          )}

          {(ownerNotice || roomOwnerResponse) && (
            <div className={`meeting-owner-notice ${roomOwnerResponse ? 'has-response' : ''}`}>
              {roomOwnerResponse ? (
                <>
                  <strong>Room owner replied</strong>
                  <span>{roomOwnerResponse.responseMessage}</span>
                </>
              ) : ownerNotice}
            </div>
          )}

          <div className={`meeting-status ${error ? 'error' : ''}`}>
            {error || status}
          </div>
          <p className="meeting-footnote">
            Anyone with this private link can join for now. Participants identify themselves by the name they enter.
          </p>
        </div>

        <div className="meeting-stage">
          {tokenData ? (
            <LiveKitRoom
              token={tokenData.token}
              serverUrl={tokenData.wsUrl}
              connect
              video
              audio
              onDisconnected={() => {
                setTokenData(null);
                setStatus('You left the meeting.');
              }}
              data-lk-theme="default"
              className="meeting-livekit-room"
            >
              <VideoConference controls={{ chat: false }} />
              <EmptyRoomOwnerNotify
                notifyingOwner={notifyingOwner}
                onNotify={notifyRoomOwner}
              />
              <RoomAudioRenderer />
            </LiveKitRoom>
          ) : (
            <div className="meeting-stage-placeholder">
              Join the room to start video. When someone else connects, they will appear here automatically.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingPage;
